import React from 'react'

import { useGetWorkOrderTasksQuery } from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useSession from '@/buyers/hooks/useSession'
import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import { UNKNOWN_ERROR } from '@/gf/providers/MsgsProvider'
import useAddPartRequest from '../hooks/useAddPartRequest'

import Action from '@/gf/components/Action'
import Modal from '@/gf/components/Modal'
import PartForm, { Errors, Form, FormErrors, initForm } from './PartForm'

const AddPartModal = ({
  rfqId,
  open,
  onClose,
  onAdded,
  jobCodeWorkOrderId,
}: {
  rfqId: string
  open: boolean
  onClose: () => void
  onAdded: () => void
  /** M5 work order this request is on — set only for AssetWorks orgs with a work
   * order, and the only case where the job-code picker has anything to offer. */
  jobCodeWorkOrderId?: string
}) => {
  const [_, msgr] = useMsgs()
  const { featureFlags } = useSession()
  const [adding, addingToggler] = useToggle(false)
  const [errors, setErrors] = React.useState<FormErrors>({})
  const [form, setForm] = React.useState<Form>(initForm)
  const addPartRequest = useAddPartRequest()

  // GEA-11818: the same rule as create (PartDetailsStep). The picker shows when the request
  // has an M5 work order and the auto-open is on; the code is required only when the work
  // order has job lines to pick from (a WO with none is the GEA-5927 case, and blocking on it
  // would strand the buyer).
  const pickerWorkOrderId =
    (!!featureFlags.assetworksAutoOpenPartRequests && jobCodeWorkOrderId) || ''

  const { data: workOrderTasksData, loading: workOrderTasksLoading } = useGetWorkOrderTasksQuery({
    client: useGqlClient(),
    variables: { workOrderId: pickerWorkOrderId },
    skip: !open || !pickerWorkOrderId,
  })

  const workOrderTasks = workOrderTasksData?.getWorkOrderTasks ?? []
  const jobCodeRequired = !!pickerWorkOrderId && workOrderTasks.length > 0
  // Until the job lines arrive, the form cannot know whether a code is required. Hold the submit,
  // or a line sent in that window opens under the work order's first open job.
  const jobCodesLoading = !!pickerWorkOrderId && workOrderTasksLoading
  // Start on the first job line, the same one the M5 open would pick today. It stays editable.
  const defaultJobCode = pickerWorkOrderId ? workOrderTasks[0]?.taskId : undefined

  React.useEffect(() => {
    if (open && defaultJobCode && !form.taskNumber)
      setForm((prev) => ({ ...prev, taskNumber: defaultJobCode }))
  }, [open, defaultJobCode, form.taskNumber])

  const onSubmit = () => {
    if (adding || jobCodesLoading) return

    if (jobCodeRequired && !form.taskNumber?.trim()) {
      setErrors({ taskNumber: 'Select a job code' })
      return
    }

    addingToggler.on()

    addPartRequest({
      rfqId,
      mpn: form.mpn,
      description: form.description,
      quantity: form.quantity,
      taskNumber: pickerWorkOrderId ? (form.taskNumber ?? null) : null,
    })
      .then(() => {
        onAdded()
        setForm(initForm)
        setErrors({})
        msgr.add('Part added.', 'positive')
      })
      .catch((err) => {
        const { graphQLErrors } = err
        const newErrors = Errors.fromGqlErrors(graphQLErrors)
        setErrors(newErrors)

        if (Object.keys(newErrors).length === 0) {
          msgr.add(err.message || UNKNOWN_ERROR, 'negative')
        }
      })
      .finally(() => {
        addingToggler.off()
      })
  }

  return (
    <Modal
      title="Add Part"
      open={open}
      onClose={onClose}
      footerAction={
        <Action.P type="submit" form="add-part" disabled={adding || jobCodesLoading}>
          {adding ? <>Adding&hellip;</> : 'Add'}
        </Action.P>
      }
    >
      <PartForm
        id="add-part"
        form={form}
        errors={errors}
        onChange={(newForm) => setForm(newForm)}
        onSubmit={onSubmit}
        jobCodeWorkOrderId={jobCodeWorkOrderId}
      />
    </Modal>
  )
}

export default AddPartModal
